import React, { useState, useCallback } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./css/ShopForm.css";

const ShopForm = () => {
  const [shopName, setShopName] = useState("");
  const [vegName, setVegName] = useState("");
  // NEW: Store English and Hindi veg names separately
  const [vegNameEng, setVegNameEng] = useState("");
  const [vegNameHin, setVegNameHin] = useState("");

  const [shopSuggestions, setShopSuggestions] = useState([]);
  const [vegSuggestions, setVegSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [shopImage, setShopImage] = useState("");
  const [shopId, setShopId] = useState(null);
  const [showNoVegSuggestions, setShowNoVegSuggestions] = useState(false);
  const [showNoShopSuggestions, setShowNoShopSuggestions] = useState(false);
  const [vegPrice, setVegPrice] = useState(null);
  const [showPriceCard, setShowPriceCard] = useState(false);

  const navigate = useNavigate();
  const BASE_URL = "https://yaasaai.com/myphpapi";

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const fetchShopSuggestions = useCallback(
    async (query) => {
      if (!query) {
        setShopSuggestions([]);
        setShowNoShopSuggestions(false);
        return;
      }
      try {
        const response = await fetch(
          `${BASE_URL}/getshopss.php?query=${encodeURIComponent(query)}`
        );
        if (!response.ok) throw new Error("Failed to fetch shops");
        const data = await response.json();
        setShopSuggestions(data.length > 0 ? data : []);
        setShowNoShopSuggestions(data.length === 0);
      } catch (err) {
        console.error("Error fetching shop suggestions:", err);
        setShopSuggestions([]);
        setShowNoShopSuggestions(false);
      }
    },
    [BASE_URL]
  );

  const fetchVegSuggestions = useCallback(
    async (query) => {
      if (!query) {
        setVegSuggestions([]);
        setShowNoVegSuggestions(false);
        return;
      }
      try {
        const response = await fetch(
          `${BASE_URL}/get-vegetables.php?query=${encodeURIComponent(query)}`
        );
        if (!response.ok) throw new Error("Failed to fetch vegetables");
        const data = await response.json();
        setVegSuggestions(data.length > 0 ? data : []);
        setShowNoVegSuggestions(data.length === 0);
      } catch (err) {
        console.error("Error fetching vegetable suggestions:", err);
        setVegSuggestions([]);
        setShowNoVegSuggestions(false);
      }
    },
    [BASE_URL]
  );

  const debouncedFetchShopsByName = useCallback(
    debounce(fetchShopSuggestions, 500),
    [fetchShopSuggestions]
  );
  const debouncedFetchVegs = useCallback(debounce(fetchVegSuggestions, 500), [
    fetchVegSuggestions,
  ]);

  const fetchShopsByVegetable = async (veg) => {
    console.log("Fetching shops for vegetable:", veg);
    try {
      const response = await fetch(
        `${BASE_URL}/getShopsByVegetable.php?veg=${encodeURIComponent(veg)}`
      );
      const data = await response.json();
      console.log("Shops data received:", data);

      if (Array.isArray(data) && data.length > 0) {
        setShopSuggestions(data);
        setShowNoShopSuggestions(false);
        setError(null);
      } else {
        setError(
          "No shop has this vegetable directly. Showing related shops instead."
        );
        fetchShopSuggestions(veg); // fallback
      }
    } catch (err) {
      console.error("Error fetching shops by vegetable:", err);
      setShopSuggestions([]);
      setShowNoShopSuggestions(true);
    }
  };

  const handleShopInputChange = (e) => {
    setShopName(e.target.value);
    setError(null);
    debouncedFetchShopsByName(e.target.value);
  };

  const handleVegInputChange = (e) => {
    setVegName(e.target.value);
    // Reset English and Hindi veg names on typing
    setVegNameEng("");
    setVegNameHin("");
    setError(null);
    debouncedFetchVegs(e.target.value);
  };

  // UPDATED: When clicking veg suggestion, set vegNameEng & vegNameHin
  const handleVegSuggestionClick = (veg) => {
    setVegName(veg.name); // input box display remains English name
    setVegNameEng(veg.name);
    setVegNameHin(veg.name_hindi || ""); // if Hindi exists, else empty string
    setVegSuggestions([]);
    setShowNoVegSuggestions(false);
    setError(null);
    fetchShopsByVegetable(veg.name);
  };

  const handleShopSuggestionClick = (shop) => {
    setShopName(shop.name || shop.shop_name || "");
    setShopSuggestions([]);
    setShowNoShopSuggestions(false);
    setError(null);
  };

  const handleSearch = async () => {
    if (!shopName || !vegNameEng) {
      setError("Shop Name and Vegetable Name are required.");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/shopsearch.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shopName, vegName: vegNameEng }),
      });

      const data = await response.json();
      console.log("my data", data);

      if (!response.ok || data.error) {
        setError(data.error || "Something went wrong");
        return;
      }

      setShopImage(data.shopImage);
      setVegPrice(data.price);
      setShopId(data.shopId);
      setShowPriceCard(true);
      setError(null);
    } catch (error) {
      console.error("Error fetching price details:", error);
      setError("Failed to fetch price details.");
    }
  };

  // UPDATED: Pass vegNameEng and vegNameHin to shop_profile on navigation
  const handleVisitShop = () => {
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) {
      alert("Please log in or provide your email.");
      return;
    }
    if (!shopId) {
      alert("Please select a shop.");
      return;
    }
    if (!vegNameEng) {
      alert("Please select a vegetable.");
      return;
    }
    navigate("/shop_profile", {
      state: {
        userEmail,
        shopId,
        searchedVegEng: vegNameEng,
        searchedVegHin: vegNameHin,
      },
    });
  };

  const handleCloseCard = () => setShowPriceCard(false);

  return (
    <Container fluid className="shopcontainer">
      <div className="shopname">
        <Row className="d-flex justify-content-center">
          <h1 className="title">Find Best Price</h1>
        </Row>

        <Form>
          <Row className="g-2 shopdata">
            <Col xs={4} lg={4} className="searchdata autocomplete">
              <Form.Control
                className="shopaddress"
                type="text"
                placeholder="Type Vegetable & Fruit Name"
                value={vegName}
                onChange={handleVegInputChange}
                autoComplete="off"
                aria-label="Vegetable or Fruit Name"
              />
              <div className="search">
                {error && !showNoShopSuggestions && (
                  <div className="error-message">{error}</div>
                )}
                {vegSuggestions.length > 0
                  ? vegSuggestions.map((veg, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => handleVegSuggestionClick(veg)}
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleVegSuggestionClick(veg);
                        }}
                      >
                        {/* Show English + Hindi if available */}
                        {veg.name} {veg.name_hindi ? `(${veg.name_hindi})` : ""}
                      </div>
                    ))
                  : showNoVegSuggestions && <div>No suggestions found</div>}
              </div>
            </Col>

            <Col xs={4} lg={4} className="searchdata">
              <Form.Control
                className="shop"
                type="text"
                placeholder="Search by Shop Name"
                value={shopName}
                onChange={handleShopInputChange}
                autoComplete="off"
                aria-label="Shop Name"
              />
              <div className="search1">
                {error && !showNoVegSuggestions && (
                  <div className="error-message">{error}</div>
                )}
                {shopSuggestions.length > 0
                  ? shopSuggestions.map((shop, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => handleShopSuggestionClick(shop)}
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            handleShopSuggestionClick(shop);
                        }}
                      >
                        {shop.name || shop.shop_name} {shop.veg_rate_1}/kg.
                      </div>
                    ))
                  : showNoShopSuggestions && <div>No shops found</div>}
              </div>
            </Col>

            <Col xs={4} lg={4} className="searchdata d-flex align-items-start">
              <Button
                className="searchbtn w-100 w-lg-50"
                variant="success"
                onClick={handleSearch}
              >
                Search
              </Button>
            </Col>
            <button
              onClick={() => navigate("/customer_reels")}
              className="btn btn-outline-success my-3"
              style={{ display: "block", margin: "0 auto", fontWeight: "bold" }}
            >
              📷 देखिए ग्राहक क्या कह रहे हैं
            </button>
          </Row>
        </Form>

        {showPriceCard && (
          <Card
            style={{ width: "8rem", height: "15rem" }}
            className="price-card"
          >
            <div
              style={{
                width: "125px", // or your preferred size
                height: "100px",

                // fallback background
              }}
            >
              <Card.Img
                variant="top"
                src={`https://yaasaai.com/ShopImages/${shopImage}`}
                alt="Shop"
              />
            </div>
            <Card.Body>
              <Card.Title className="shop_title" style={{ fontSize: "10px" }}>
                Price of {vegNameEng} {vegNameHin && `(${vegNameHin})`} at Shop
              </Card.Title>
              <Card.Title>Price: ₹{vegPrice}</Card.Title>
              <Button
                className="btnvisit"
                onClick={handleVisitShop}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  backgroundColor: "#4CAF50", // green and attractive
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  transition: "0.3s",
                }}
                title="visit shop now"
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#45a049")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "#4CAF50")}
              >
                Visit Now
              </Button>
              <Button
                variant="danger"
                onClick={handleCloseCard}
                style={{
                  marginTop: "5px",
                  padding: "4px 8px",
                  fontSize: "10px",
                  backgroundColor: "red", // green and attractive
                  color: "white",
                  border: "none",
                  borderRadius: "20px",
                  cursor: "pointer",
                }}
                title="cancel now"
                onMouseOver={(e) => (e.target.style.backgroundColor = "red")}
                onMouseOut={(e) => (e.target.style.backgroundColor = "red")}
              >
                X
              </Button>
            </Card.Body>
          </Card>
        )}
      </div>
    </Container>
  );
};

export default ShopForm;
